/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { useOtMutation } from "../../hooks/mutations/useOtMutation";
import { adminMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/adminMutationKeys";
import {
  Breadcrumbs,
  Divider,
  Drawer,
  Paper,
  Alert,
  Snackbar,
  Box,
  OutlinedInput,
  FormControl,
  Tooltip,
  InputLabel,
  InputAdornment,
 } from "@mui/material";
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import {
  StyledBreadCrumbContainer,
  StyledLink,
  StyledTitleContainer,
  StyledTitle,
  StyledSearchBar,
  StyledCreateButton,
  StyledCancelButton,
  StyledAddButton,
  StyledButtonContainer,
  StyledCreateTitle,
  StyledFormContainer,
  StyledFormFieldText,
  StyledTextField,
  StyledHelperText,
  StyledPhoneField,
  RestaurantAdminContainer,
  StyledUpdateButton,
  StyledSelect,
  StyledInputLabel,
  StyledAddInstanceButton,
  AddInstanceButtonText,
  StyledInstanceContainer,
  StyledResourceSelect,
  StyledInstanceSelect,
  StyledRoleSelect,
  StyledInstanceWrapper,
  StyledChip,
  StyledTab,
  StyledTabPanel,
 } from "./styles";
import AddIcon from '@mui/icons-material/Add';
import api from "../../api";
import authenticatedFetch from "../../api/authenticatedFetch";
import { RestaurantAdminsTable } from "../../components/restaurant_admins_table";
import { LoadingSpinner } from '../../components/loading_spinner/index'
import { AbilityContext } from '../../contexts/abilityContext'
import { strictCanScope } from "../../utils/caslStrict";
import { Loader } from "../../components/loader";
import { useInfiniteQuery, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { InfoCircleOutlined } from "@ant-design/icons";
import ClearIcon from '@mui/icons-material/Clear';
import { useNotificationContext } from "../../contexts/notificationContext";
import { capitalize, startCase } from 'lodash'
import { useSelector, useDispatch } from "react-redux";
import { fetchAdmin } from "../../store/adminSlice";
import { checkAdmin } from "../../utils/checkAdmin";
import DropdownSearchBar from "../../components/dorpdown_searchbar";
import { SelectedMenuItem } from "../../components/selected_menu_item/index"
import dayjs from "dayjs";
import SearchIcon from '@mui/icons-material/Search';
import MfaConfigurationModal from '../../components/mfa_configuration_modal';
import { StyledMenuItem } from "../../components/styled_menu_item";
import { Query_Keys } from "../../lib/queryKeys";
import { normalizeSearchTerm } from "../../utils/normalizeSearchTerm";
import { useDebounce } from "../../utils/useDebounce";
import { buildQueryParams } from "../../utils/buildQueryParams";
import { useInfiniteScrollObserver } from "../../hooks/useInfiniteScrollObserver";
import { PerPage } from "../../constants/perPage";
import {
  getDropdownNextPageParam,
  mergeOptionsIfChanged,
  mergeUniqueByKey,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../utils/dropdownInfiniteQuery";
import { DropdownInfiniteLoader } from "../../components/dropdown_infinite_loader";

let id = 100
const drawerWidth = 570
const itemHeight = 28;
const itemPaddingTop = 5;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: itemHeight * 4.5 + itemPaddingTop,
      width: 250,
    },
  },
};

const topLevelAccess = [
  {
    name: 'Admin',
    value: 'admin',
  },
  {
    name: 'Viewer',
    value: 'viewer'
  },
];

const resourceType = [
  {
    name: 'Restaurant Group',
    value: 'Restaurant_Group',
  },
  {
    name: 'Restaurant',
    value: 'Restaurant',
  },
]

const resourceRole = {
  Restaurant_Group: [
    {
      name: 'Admin',
      value: 'admin',
    },
    {
      name: 'Viewer',
      value: 'viewer',
    }
  ],
  Restaurant: [
    {
      name: 'Admin',
      value: 'admin',
    },
    {
      name: 'Viewer',
      value: 'viewer',
    }
  ]
}

export const UserManagement = () => {
  const { admin } = useSelector((state) => state.admin);
  const dispatch = useDispatch();
  const flattenDropdownPages = (data) => data.pages.flat();

  const { mutateAsync: createAdminMutation } = useOtMutation({
    mutationFn: async (payload) => await api.post('/v1/admin/admins', payload),
    invalidateKeys: adminMutationKeyGroups.create,
    onSuccess: () => {
      setOpen(false)
      setSuccessOpen(true)
      setStatus(false)
    }
  });

  const { mutateAsync: updateAdminMutation } = useOtMutation({
    mutationFn: async ({ id, payload }) => await api.put(`/v1/admin/admins/${id}`, payload),
    invalidateKeys: adminMutationKeyGroups.update,
    onSuccess: (data, variables) => {
      setOpenEdit(false)
      setEditSuccess(true)
      setStatus(false)
      setInstanceRole([])
      
      if (admin && variables.id === admin.id) {
        dispatch(fetchAdmin());
      }
    }
  });

  const { mutateAsync: updateBulkMfaMutation } = useOtMutation({
    mutationFn: async (payload) => {
      if (typeof payload.enabled !== 'boolean') {
        throw new Error('enabled must be a boolean');
      }
      if (payload.enforcement && !['mandatory', 'optional'].includes(payload.enforcement)) {
        throw new Error('enforcement must be mandatory or optional');
      }
      return await api.post('/v1/admin/admins/mfa/bulk_toggle_enabled', {
        mfa: {
          enabled: payload.enabled,
          ...(payload.enforcement ? { enforcement: payload.enforcement } : {}),
        },
      });
    },
    invalidateKeys: adminMutationKeyGroups.update,
    successMessage: ({ variables }) => {
      const action = variables.enabled ? 'enabled' : 'disabled';
      const type = variables.enabled && variables.enforcement ? ` (${variables.enforcement})` : '';
      return `MFA ${action} for eligible admins${type}`;
    },
    errorMessage: ({ error }) => {
      return error?.response?.data?.error || 'Failed to update MFA configuration';
    },
    onSuccess: () => {
      setMfaConfigModalOpen(false);
    },
    onSettled: () => setBulkMfaLoading(false)
  });

  const [tab, setTab] = useState('1');
  const [globalMfaEnabled, setGlobalMfaEnabled] = useState(null);
  const [open, setOpen] = useState(false)
  const [successOpen, setSuccessOpen] = useState(false)
  const [errorOpen, setErrorOpen] = useState(false)
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [tabSortState, setTabSortState] = useState({
    '1': { sortBy: '', sortOrder: 'asc' },
    '2': { sortBy: '', sortOrder: 'asc' },
  });
  const [bulkMfaLoading, setBulkMfaLoading] = useState(false);
  const [globalMfaEnforcement, setGlobalMfaEnforcement] = useState('mandatory');
  const [openEdit, setOpenEdit] = useState(false);
  const [updateAdmin, setUpdateAdmin] = useState({})
  const [editSuccess, setEditSuccess] = useState(false)
  const [editRoles, setEditRoles] = useState({})
  const [search, setSearch] = useState('');
  const [searchTexts, setSearchTexts] = useState({});
  const [restaurantSearch, setRestaurantSearch] = useState('');
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState('');
  const [restaurantAdmin, setRestaurantAdmin] = useState({
    first_name: '',
    last_name: '',
    phone_no: '',
    email: '',
    username: '',
    top_level_access: [],
    instance_level_access: []
  })
  const [error, setError] = useState({
    first_name: '',
    last_name: '',
    phone_no: '',
    email: '',
    username: ''
  })
  const [editError, setEditError] = useState({
    first_name: '',
    last_name: '',
    phone_no: '',
    email: '',
    username: ''
  })
  
  const superAdmin = checkAdmin(admin?.formatted_roles)
  const [status, setStatus] = useState(false)
  const [fetchList, setFetchList] = useState(false)
  const [csvLoading, setCsvLoading] = useState(false)
  const { ability } = useContext(AbilityContext)
  const createUser = strictCanScope(ability, 'create', 'Restaurant_Group');
  const readPermission = strictCanScope(ability, 'read', 'Restaurant_Group');
  const queryClient = useQueryClient();
  const showNotification = useNotificationContext();
  const [topLevelRole, setTopLevelRole] = useState([]);
  const [instanceRole, setInstanceRole] = useState([]);
  const [referenceTopLevelRole, setReferenceTopLevelRole] = useState([])
  const [updateTopLevelRole, setUpdateTopLevelRole] = useState([])
  const [referenceInstanceLevelRole, setReferenceInstanceLevelRole] = useState([])
  const [mfaConfigModalOpen, setMfaConfigModalOpen] = useState(false);
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([])
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300)
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300)

  React.useEffect(() => {
    const fetchGlobalMfaStatus = async () => {
      try {
        const response = await api.get('/v1/admin/admins/global_mfa_status');
        setGlobalMfaEnabled(response?.data?.global_mfa_enabled);
        setGlobalMfaEnforcement(response?.data?.global_mfa_enforcement || 'mandatory');
      } catch (err) {
        setGlobalMfaEnabled(null);
      }
    };
    fetchGlobalMfaStatus();
  }, []);

  const itemHeight = 28;
  const itemPaddingTop = 5;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: itemHeight * 4.5 + itemPaddingTop,
        width: 250,
      },
    },
  };

  useEffect(() => {
    if(editRoles?.length > 0) {
      const topLevelRoles = editRoles?.filter((data) => {
        if((data?.role === 'admin' || data?.role === 'viewer') && !(data?.instance)){
          return data
        }
      })
      let roleList = []
      topLevelRoles?.forEach((data) => {
        roleList.push(data?.role)
      })

      const instanceLevelRoles = editRoles?.filter((data) => {
        if(data?.instance){
          return data
        }
      })

      const instanceRolesList = formatInstanceRole(instanceLevelRoles)
      if(instanceRolesList.length > 0){
        setInstanceRole(instanceRolesList)
        setReferenceInstanceLevelRole(instanceRolesList)
      }
      setReferenceTopLevelRole(roleList)
      setUpdateTopLevelRole(roleList)
    }
  }, [editRoles])

  const normalizedSearchQuery = useMemo(() => normalizeSearchTerm(search), [search]);
  const debouncedSearchQuery = useDebounce(normalizedSearchQuery, 300);
  const hasActiveSearch = debouncedSearchQuery.length > 0;
  const isSearchLoading = normalizedSearchQuery.length > 0 && normalizedSearchQuery !== debouncedSearchQuery;

  const fetchAdmins = useCallback(async () => {
    const endpoint = tab === '2' ? '/v1/admin/admins/quarantined' : '/v1/admin/admins?role=restaurant_admin';
    const currentSort = tabSortState[tab] || { sortBy: '', sortOrder: 'asc' };
    const response = await api.get(endpoint, {
      params: {
        per_page: itemsPerPage,
        page: page,
        ...(debouncedSearchQuery ? { search: debouncedSearchQuery } : {}),
        ...(currentSort.sortBy ? { sort_by: currentSort.sortBy, sort_order: currentSort.sortOrder } : {}),
      }
    })
    return { data: response?.data, pageCount: response?.headers["total-count"] }
  }, [tab, itemsPerPage, page, tabSortState, debouncedSearchQuery])

  const handleSortChange = useCallback((nextSortBy, nextSortOrder) => {
    setTabSortState((prev) => ({
      ...prev,
      [tab]: {
        sortBy: nextSortBy,
        sortOrder: nextSortOrder,
      },
    }));
    setPage(1);
  }, [tab]);

  const { data: adminUsers, isFetching } = useQuery({
    queryKey: Query_Keys.admins({
      page: page,
      itemsPerPage: itemsPerPage,
      tab: tab,
      search: debouncedSearchQuery,
      sortBy: tabSortState[tab]?.sortBy,
      sortOrder: tabSortState[tab]?.sortOrder,
    }),
    queryFn: fetchAdmins,
    staleTime: 10 * 60 * 1000,
  })

  const usersForTable = adminUsers?.data || [];
  const usersPageCount = Number(adminUsers?.pageCount || 0);
  const isInitialUsersLoad = !adminUsers && isFetching && normalizedSearchQuery.length === 0;
  const disableCsvDownloadButton = usersForTable.length === 0 || csvLoading;

  const handleSearch = useCallback((searchQuery) => {
    setSearch(searchQuery);
    setPage(1);
  }, []);

  const fetchRestaurantGroups = useCallback(async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurant_groups_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    })

    return response?.data?.map((group) => {
      const normalizedGroup = normalizeRestaurantGroup(group)

      return {
        id: normalizedGroup?.label,
        key: normalizedGroup?.label,
        value: normalizedGroup?.value,
      }
    }) || []
  }, [debouncedRestaurantGroupSearch])

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({
      search: debouncedRestaurantGroupSearch,
      source: 'user_management_instance',
    }),
    queryFn: fetchRestaurantGroups,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: flattenDropdownPages,
    staleTime: 10 * 60 * 1000,
    enabled: fetchList,
    placeholderData: keepPreviousData,
  })

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  })

  const fetchRestaurants = useCallback(async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurants_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
      },
    })

    return response?.data?.map((restaurant) => {
      const normalizedRestaurant = normalizeRestaurant(restaurant)

      return {
        id: normalizedRestaurant?.id,
        key: normalizedRestaurant?.id,
        value: normalizedRestaurant?.name,
      }
    }) || []
  }, [debouncedRestaurantSearch])

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: null,
      search: debouncedRestaurantSearch,
      source: 'user_management_instance',
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: flattenDropdownPages,
    staleTime: 10 * 60 * 1000,
    enabled: fetchList,
    placeholderData: keepPreviousData,
  })

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  })

  const handleDrawerOpen = useCallback(() => {
    setOpen(true)
    setFetchList(true)
  }, [])

  const handleDrawerClose = useCallback(() => {
    setOpen(false)
    setFetchList(false)
    setRestaurantSearch('')
    setRestaurantGroupSearch('')
    setRestaurantAdmin({
      first_name: '',
      last_name: '',
      phone_no: '',
      email: '',
      username: '',
      top_level_access: [],
      instance_level_access: [],
    })
    setError({
      first_name: '',
      last_name: '',
      phone_no: '',
      email: '',
      username: ''
    })
    setTopLevelRole([])
    setInstanceRole([])
  }, [])

  const change = (value, field) => {
    setRestaurantAdmin({...restaurantAdmin, [field]: value})
    if(error[field] !== '') setError({...error, [field]: ''})
  }

  const update = (value, field) => {
    setUpdateAdmin({...updateAdmin, [field]: value})
    if(editError[field] !== '') setEditError({...editError, [field]: ''})
  }

  const ERROR_MESSAGES = {
    unauthorized: "You don't have permission to perform this action",
    mailer: 'Admin is created but failed to send an email'
  }

  const getErrorMessage = (errors = {}) => {
    const key = Object.keys(errors)?.[0]
    return Object.hasOwn(ERROR_MESSAGES, key) ? { errorMessage: ERROR_MESSAGES[key], errorKey: key } : ''
  }

  const handleAdd = useCallback(async () => {
    setStatus(true)
    const payload = {
      ...restaurantAdmin,
      phone_no: restaurantAdmin?.phone_no.length <= 4 ? '' : restaurantAdmin?.phone_no,
    }

    if(instanceRole?.length > 0) {
      const validData = checkInstanceRoleData()
      if(validData?.isValid){
        payload.instance_level_access = transformInstanceRoleData()
      }
      else {
        showNotification('Instance level roles cannot be blank', 'error')
        setStatus(false)
        return
      }
    }
    try {
      await createAdminMutation(payload);
    } catch (error) {
      const errors = error?.response?.data?.errors
      const apiError = getErrorMessage(errors)

      if(!apiError) {
        setError(errors)
        setStatus(false)
        return
      }

      if(apiError?.errorKey === 'mailer') {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['admins'] }),
          queryClient.invalidateQueries({ queryKey: ['admin_user_dropdown'] })
        ])
        showNotification(`User '${restaurantAdmin?.first_name}' has been created successfully but failed to send an email`, 'info')
        setOpen(false)
        setStatus(false)
        return
      }

      showNotification(apiError?.errorMessage, 'error')
      setStatus(false)
    }
  }, [restaurantAdmin, instanceRole])

  const handleUpdate = useCallback(async () => {
    setStatus(true)
    const originalRoles = referenceTopLevelRole || [];
    const updatedRoles = updateTopLevelRole || [];

    const originalInstanceRoles = referenceInstanceLevelRole || [];
    const updatedInstanceRoles = instanceRole || [];

    const addedRoles = updatedRoles.filter(role => !originalRoles.includes(role));
    const removedRoles = originalRoles.filter(role => !updatedRoles.includes(role));

    const addedInstanceRoles = handleUpdateInstanceRole(originalInstanceRoles, updatedInstanceRoles)
    const removedInstanceRoles = handleRemoveInstanceRole(originalInstanceRoles, updatedInstanceRoles)
    if(addedInstanceRoles?.length > 0){
      const validData = checkInstanceRoleData()
      if(validData?.isValid === false) {
        setStatus(false)
        showNotification('Instance level roles cannot be blank', 'error')
        return
      }
    }

    const updatePayload = {
      ...updateAdmin,
      ...(addedRoles.length > 0 && { top_level_access: addedRoles }),
      ...(removedRoles.length > 0 && { delete_top_level: removedRoles }),
      ...(addedInstanceRoles?.length > 0 && {instance_level_access: addedInstanceRoles}),
      ...(removedInstanceRoles?.length > 0 && {delete_instance_level: removedInstanceRoles})
    };

    if(updateAdmin?.phone_no.length <= 4){
      updateAdmin.phone_no = ''
    }

    try {
      await updateAdminMutation({ id: updateAdmin?.id, payload: updatePayload })
    } catch (error) {
      const errors = error?.response?.data?.errors || {}
      const errorKeys = Object.keys(errors)
      if(errorKeys[0] === 'unauthorized'){
        setOpenEdit(false)
        setErrorOpen(true)
      }
      else if(errorKeys.includes('role')){
        const roleErrors = errors.role
        const errorMessage = Array.isArray(roleErrors) ? roleErrors[0] : roleErrors
        showNotification(errorMessage, 'error')
      }
      else{
        setEditError(errors)
      }
      setStatus(false)
    }
  }, [updateAdmin, referenceTopLevelRole, updateTopLevelRole, referenceInstanceLevelRole, instanceRole])

  const handleClose = useCallback(() => {
    setSuccessOpen(false)
    setRestaurantAdmin({
      first_name: '',
      last_name: '',
      phone_no: '',
      email: '',
      username: '',
      top_level_access: [],
      instance_level_access: [],
    })
    setTopLevelRole([])
    setInstanceRole([])
  }, [])

  const handleEditClose = useCallback(() => {
    setOpenEdit(false)
    setRestaurantSearch('')
    setRestaurantGroupSearch('')
    setEditError({
      first_name: '',
      last_name: '',
      phone_no: '',
      email: '',
      username: ''
    })
    setUpdateAdmin({})
    setUpdateTopLevelRole([])
    setInstanceRole([])
    setReferenceInstanceLevelRole([])
  }, [])

  const handleTopLevelChange = (event) => {
    const {
      target: { value },
    } = event;
    setTopLevelRole(
      typeof value === 'string' ? value.split(',') : value,
    );
    setRestaurantAdmin({...restaurantAdmin, top_level_access: typeof value === 'string' ? value.split(',') : value})
  };

  const handleTopLevelDelete = (value) => {
    const topLevelRoles = topLevelRole.filter((role) => role !== value)
    setTopLevelRole(topLevelRoles)
    setRestaurantAdmin({...restaurantAdmin, top_level_access: topLevelRoles})
  }

  const addInstanceRole = () => {
    id++
    setInstanceRole([...instanceRole, {id: id, resourceType: '', resourceInstance: '', resourceRole: ''}])
  }

  const removeInstanceRole = (id) => {
    const instanceRoles = instanceRole.filter((instance) => instance?.id !== id)
    setInstanceRole(instanceRoles)
  }

  const handleResourceType = (id, resource) => {
    let instanceResource = instanceRole?.map((instance) => {
      if(instance?.id === id) {
        return {...instance, resourceType: resource, resourceRole: ''}
      }
      return instance
    })
    setInstanceRole(instanceResource)
  }

  const handleResourceRole = (id, role) => {
    let instanceRoles = instanceRole?.map((instance) => {
      if(instance?.id === id) {
        return {...instance, resourceRole: role}
      }
      return instance
    })
    setInstanceRole(instanceRoles)
  }

  const handleResourceInstance = (id, instance) => {
    let instanceKey = instanceRole?.map((key) => {
      if(key?.id === id) {
        return { ...key, resourceInstance: instance }
      }
      return key
    })
    setInstanceRole(instanceKey)
  }

  const checkInstanceRoleData = () => {
    for(const obj of instanceRole){
      for(const key in obj){
        if (obj[key] === '') {
          return { isValid: false, invalidId: obj.id };
        }
      }
    }
    return { isValid: true };
  }

  const transformInstanceRoleData = () => {
    const transformed = instanceRole.map( obj => ({
      resource_instance: `${obj.resourceType}:${obj.resourceInstance}`,
      role: `${obj.resourceRole}`,
    }))
    return transformed
  }

  // Edit User Functions
  const handleEditTopLevelChange = (event) => {
     const {
      target: { value },
    } = event;
    setUpdateTopLevelRole(
      typeof value === 'string' ? value.split(',') : value,
    );
  }

  const handleEditTopLevelDelete = (value) => {
    const topLevelRoles = updateTopLevelRole.filter((role) => role !== value)
    setUpdateTopLevelRole(topLevelRoles)
  }

  const formatInstanceRole = (role) => {
    let id = 0
    let formattedInstanceRoles = []
    role?.forEach((data) => {
      id++
      let instance = data?.instance.split(':')
      formattedInstanceRoles.push(
        {
          id: id,
          resourceType: instance?.[0],
          resourceInstance: instance?.[1],
          resourceRole: data?.role,
        }
      )
    })
    return formattedInstanceRoles
  }

  useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, restaurantGroups, 'id', ['value']))
  }, [restaurantGroups])

  useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, restaurants, 'id', ['value']))
  }, [restaurants])

  useEffect(() => {
    const instanceLevelRoles = Array.isArray(editRoles) ? editRoles.filter((role) => role?.instance) : []
    const instanceLevelDisplayRoles = updateAdmin?.roles?.instance_level

    if (instanceLevelRoles.length === 0 || !Array.isArray(instanceLevelDisplayRoles)) {
      return
    }

    const nextRestaurantGroupOptions = []
    const nextRestaurantOptions = []

    instanceLevelRoles.forEach((roleData, index) => {
      const [resourceType, instanceId] = `${roleData?.instance || ''}`.split(':', 2)
      const displayRole = instanceLevelDisplayRoles[index]

      if (!resourceType || !instanceId || !displayRole) {
        return
      }

      const separatorIndex = displayRole.lastIndexOf('#')
      const resourceLabel = separatorIndex >= 0 ? displayRole.slice(0, separatorIndex) : displayRole
      const resourcePrefix = `${resourceType}:`
      const displayName = resourceLabel.startsWith(resourcePrefix)
        ? resourceLabel.slice(resourcePrefix.length)
        : ''

      if (!displayName) {
        return
      }

      const option = {
        id: instanceId,
        key: instanceId,
        value: displayName,
      }

      if (resourceType === 'Restaurant_Group') {
        nextRestaurantGroupOptions.push(option)
      }

      if (resourceType === 'Restaurant') {
        nextRestaurantOptions.push(option)
      }
    })

    if (nextRestaurantGroupOptions.length > 0) {
      setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, nextRestaurantGroupOptions, 'id'))
    }

    if (nextRestaurantOptions.length > 0) {
      setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, nextRestaurantOptions, 'id'))
    }
  }, [editRoles, updateAdmin?.roles?.instance_level])

  const missingRestaurantGroupIds = useMemo(() => [...new Set(
    instanceRole
      .filter((instance) => instance?.resourceType === 'Restaurant_Group' && instance?.resourceInstance)
      .map((instance) => `${instance.resourceInstance}`)
      .filter((instanceId) => !restaurantGroupOptionsCache?.some((option) => `${option?.id}` === instanceId))
  )], [instanceRole, restaurantGroupOptionsCache])

  const missingRestaurantIds = useMemo(() => [...new Set(
    instanceRole
      .filter((instance) => instance?.resourceType === 'Restaurant' && instance?.resourceInstance)
      .map((instance) => `${instance.resourceInstance}`)
      .filter((instanceId) => !restaurantOptionsCache?.some((option) => `${option?.id}` === instanceId))
  )], [instanceRole, restaurantOptionsCache])

  const missingRestaurantGroupIdsSignature = missingRestaurantGroupIds.join(',')
  const missingRestaurantIdsSignature = missingRestaurantIds.join(',')

  useEffect(() => {
    if (!openEdit || instanceRole.length === 0) {
      return
    }

    if (missingRestaurantGroupIds.length === 0 && missingRestaurantIds.length === 0) {
      return
    }

    let cancelled = false

    const hydrateMissingInstanceLabels = async () => {
      const [restaurantGroupResults, restaurantResults] = await Promise.all([
        Promise.all(missingRestaurantGroupIds.map(async (instanceId) => {
          try {
            const response = await api.get(`/v1/admin/restaurant_groups/${instanceId}`)
            return {
              id: `${response?.data?.id ?? instanceId}`,
              key: `${response?.data?.id ?? instanceId}`,
              value: response?.data?.name,
            }
          } catch {
            return null
          }
        })),
        Promise.all(missingRestaurantIds.map(async (instanceId) => {
          try {
            const response = await api.get(`/v1/admin/restaurants/${instanceId}`)
            return {
              id: `${response?.data?.id ?? instanceId}`,
              key: `${response?.data?.id ?? instanceId}`,
              value: response?.data?.name,
            }
          } catch {
            return null
          }
        })),
      ])

      if (cancelled) {
        return
      }

      const validRestaurantGroups = restaurantGroupResults.filter((option) => option?.value)
      const validRestaurants = restaurantResults.filter((option) => option?.value)

      if (validRestaurantGroups.length > 0) {
        setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, validRestaurantGroups, 'id'))
      }

      if (validRestaurants.length > 0) {
        setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, validRestaurants, 'id'))
      }
    }

    hydrateMissingInstanceLabels()

    return () => {
      cancelled = true
    }
  }, [missingRestaurantGroupIdsSignature, missingRestaurantIdsSignature, openEdit])

  const selectedRestaurantGroupOptions = restaurantGroupOptionsCache?.filter((restaurantGroup) => (
    instanceRole?.some((instance) => instance?.resourceType === 'Restaurant_Group' && `${instance?.resourceInstance}` === `${restaurantGroup?.id}`)
  ))

  const displayedRestaurantGroups = mergeUniqueByKey(restaurantGroups, selectedRestaurantGroupOptions, 'id')

  const selectedRestaurantOptions = restaurantOptionsCache?.filter((restaurant) => (
    instanceRole?.some((instance) => instance?.resourceType === 'Restaurant' && `${instance?.resourceInstance}` === `${restaurant?.id}`)
  ))

  const displayedRestaurants = mergeUniqueByKey(restaurants, selectedRestaurantOptions, 'id')

  const getList = useCallback((resource) => {
    switch(resource) {
      case 'Restaurant_Group': {
        return restaurantGroupSearch.trim() ? restaurantGroups : displayedRestaurantGroups
      }
      case 'Restaurant': {
        return restaurantSearch.trim() ? restaurants : displayedRestaurants
      }
      default: {
        return []
      }
    }
  }, [displayedRestaurantGroups, displayedRestaurants, restaurantGroupSearch, restaurantGroups, restaurantSearch, restaurants])

  const handleUpdateInstanceRole = (originalInstanceRoles, addedInstanceRoles) => {
    const formattedRoles = transformInstance(originalInstanceRoles)
    const formattedAddedRoles = transformInstance(addedInstanceRoles)
    const newAddedInstanceRoles = formattedAddedRoles?.filter((role) => !formattedRoles.includes(role))
    let tranformInstanceRole = []

    newAddedInstanceRoles?.forEach((role) => {
      const instance = (role?.split('#'))
      tranformInstanceRole.push({ resource_instance: instance?.[0], role: instance?.[1] })
    })

    return tranformInstanceRole
  }

  const handleRemoveInstanceRole = (originalInstanceRoles, removedInstanceRoles) => {
    const formattedRoles = transformInstance(originalInstanceRoles)
    const formattedRemovedRoles = transformInstance(removedInstanceRoles)
    const removedRoles = formattedRoles?.filter((role) => !formattedRemovedRoles.includes(role))
    let tranformInstanceRole = []

    removedRoles?.forEach((role) => {
      const instance = (role?.split('#'))
      tranformInstanceRole.push({ resource_instance: instance?.[0], role: instance?.[1] })
    })

    return tranformInstanceRole
  }

  const handleAdminCsvExport = async () => {
    try {
      setCsvLoading(true);
      const panel =  tab === '2' ? 'quarantined_admins' : 'admins'
      const searchQuery = debouncedSearchQuery || normalizedSearchQuery;
      const query = buildQueryParams({ ...(searchQuery ? { search: searchQuery } : {}) });
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=${panel}${query ? `&${query}` : ''}`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date
      const searchSuffix = searchQuery
        ? `_search-${searchQuery.replace(/\s+/g, '-').slice(0, 40)}`
        : '';
      let filename = `${panel}${searchSuffix}_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('CSV download error:', error);
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  }

  const transformInstance = useCallback((data) => {
    let roles = []
    data?.forEach((role) => {
      roles.push(`${role?.resourceType}:${role?.resourceInstance}#${role?.resourceRole}`)
    })
    return roles
  }, [])

  const handleTabChange = useCallback((event, newValue) => {
    setTab(newValue);
    setPage(1);
    setItemsPerPage(25);
  }, [])

  const getInstanceName = useCallback((resourceType, instanceId) => {
    if(resourceType === 'Restaurant_Group') {
      const restaurantGroup = restaurantGroupOptionsCache?.find((res) => `${res?.id}` === `${instanceId}`)
      return restaurantGroup ? restaurantGroup?.value : ''
    }
    if(resourceType === 'Restaurant') {
      const restaurant = restaurantOptionsCache?.find((res) => `${res?.id}` === `${instanceId}`)
      return restaurant ? restaurant?.value : ''
    }
  }, [restaurantGroupOptionsCache, restaurantOptionsCache])

  const handleOpenMfaConfig = () => {
    setMfaConfigModalOpen(true);
  };

  const handleCloseMfaConfig = () => {
    setGlobalMfaEnabled(globalMfaEnabled);
    setGlobalMfaEnforcement(globalMfaEnforcement);
    setMfaConfigModalOpen(false);
  };

  const handleSaveMfaConfig = async (enabled, enforcement = null) => {
    setBulkMfaLoading(true);
    try {
      await updateBulkMfaMutation({ enabled: enabled, enforcement: enabled && enforcement ? enforcement : undefined });
      setGlobalMfaEnabled(enabled);
      if (enabled) {
        setGlobalMfaEnforcement(enforcement);
      }
    } catch (err) {
      // Error handled in mutation onError
    }
  };

  return(
    <RestaurantAdminContainer>
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink 
            LinkComponent={StyledLink} 
            label="Home" to="/admin/dashboard" 
          />
          <StyledLink color={'#515152'}>
            User Management
          </StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      {/* Success Alert */}
      <Snackbar
        open={successOpen}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={handleClose}
          severity="success"
          variant="filled"
          sx={{width: '100%'}}
        >
          {`User '${restaurantAdmin?.first_name}' has been created successfully`}
        </Alert>
      </Snackbar>

      <Snackbar
        open={editSuccess}
        autoHideDuration={4000}
        onClose={() => setEditSuccess(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={() => setEditSuccess(false)}
          severity="success"
          variant="filled"
          sx={{width: '100%'}}
        >
          {`User '${updateAdmin?.first_name}' has been updated successfully`}
        </Alert>
      </Snackbar>

      {/* Error Alert */}
      <Snackbar
        open={errorOpen}
        autoHideDuration={4000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={() => setErrorOpen(false)}
          severity="error"
          variant="filled"
          sx={{width: '100%'}}
        >
          {"You don't have permissions to create or edit user"}
        </Alert>
      </Snackbar>

      <StyledTitleContainer>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <StyledTitle>Users</StyledTitle>

          <StyledSearchBar
            value={search}
            placeholder="Search users"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={(e) => handleSearch(e.target.value)}
            autoComplete="off"
            inputProps={{ 'data-testid': 'users-search-input' }}
          />
        </div>
      </StyledTitleContainer>

      <Divider sx={{ borderStyle: 'solid', borderWidth: 1 }} />

      <Box sx={{ padding: '25px 0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <StyledCreateButton
          onClick={handleDrawerOpen}
          variant="contained"
          startIcon={<AddIcon/>}
          disabled={!(superAdmin?.role === 'admin') || tab === '2'}
        >
          Create User
        </StyledCreateButton>

        <div style={{ display: 'flex', gap: 8 }}>
          <StyledCreateButton
            onClick={handleOpenMfaConfig}
            variant="contained"
            disabled={!(superAdmin?.role === 'admin') || tab === '2'}
          >
            {globalMfaEnabled ? 'Manage MFA' : 'Enable MFA'}
          </StyledCreateButton>
        </div>
      </Box>

      {/* MFA Configuration Modal */}
      <MfaConfigurationModal
        open={mfaConfigModalOpen}
        onClose={handleCloseMfaConfig}
        onSave={handleSaveMfaConfig}
        currentStatus={globalMfaEnabled}
        currentEnforcement={globalMfaEnforcement}
        loading={bulkMfaLoading}
      />

      {/* Tabs */}
      <TabContext value={tab}>
        <Box sx={{ borderBottom: 0, borderColor: 'divider' }}>
          <TabList onChange={handleTabChange} aria-label="user management tabs">
            <StyledTab label="Active Users" value="1" sx={{textTransform: 'none'}}/>
            <StyledTab label="Quarantined Users" value="2" />
          </TabList>
        </Box>
        <StyledTabPanel value="1">
          {
            isInitialUsersLoad ? (
              <Loader />
            ) : (
              !readPermission ? (
                <div></div>
              ) : (
                <React.Fragment>
                  <RestaurantAdminsTable
                    open={open}
                    data={usersForTable}
                    fetchAdmins={fetchAdmins}
                    itemsPerPage={itemsPerPage}
                    setItemsPerPage={setItemsPerPage}
                    page={page}
                    setPage={setPage}
                    pageCount={usersPageCount}
                    fetchingLoader={isInitialUsersLoad}
                    searchLoading={isSearchLoading || (isFetching && hasActiveSearch)}
                    hasSearchTerm={normalizedSearchQuery.length > 0}
                    edit={openEdit}
                    setEdit={setOpenEdit}
                    setUpdateAdmin={setUpdateAdmin}
                    setEditError={setEditError}
                    setEditRoles={setEditRoles}
                    setFetch={setFetchList}
                    tab={tab}
                    showDownloadButton={true}
                    disableDownloadButton={disableCsvDownloadButton}
                    handleExportCsv={handleAdminCsvExport}
                    csvLoading={csvLoading}
                    showNotification={showNotification}
                    sortBy={tabSortState[tab]?.sortBy}
                    sortOrder={tabSortState[tab]?.sortOrder}
                    onSortChange={handleSortChange}
                  />
                </React.Fragment>
              )
            )
          }
        </StyledTabPanel>
        <StyledTabPanel value="2">
          {
            isInitialUsersLoad ? (
              <Loader />
            ) : (
              !readPermission ? (
                <div></div>
              ) : (
                <React.Fragment>
                  <RestaurantAdminsTable
                    open={open}
                    data={usersForTable}
                    fetchAdmins={fetchAdmins}
                    itemsPerPage={itemsPerPage}
                    setItemsPerPage={setItemsPerPage}
                    page={page}
                    setPage={setPage}
                    pageCount={usersPageCount}
                    fetchingLoader={isInitialUsersLoad}
                    searchLoading={isSearchLoading || (isFetching && hasActiveSearch)}
                    hasSearchTerm={normalizedSearchQuery.length > 0}
                    edit={openEdit}
                    setEdit={setOpenEdit}
                    setUpdateAdmin={setUpdateAdmin}
                    setEditError={setEditError}
                    setEditRoles={setEditRoles}
                    setFetch={setFetchList}
                    tab={tab}
                    showDownloadButton={true}
                    disableDownloadButton={disableCsvDownloadButton}
                    handleExportCsv={handleAdminCsvExport}
                    csvLoading={csvLoading}
                    showNotification={showNotification}
                    sortBy={tabSortState[tab]?.sortBy}
                    sortOrder={tabSortState[tab]?.sortOrder}
                    onSortChange={handleSortChange}
                  />
                </React.Fragment>
              )
            )
          }
        </StyledTabPanel>
      </TabContext>

      {/* Create Restaurant Admin Drawer */}
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            marginTop: 'calc(35px * 2)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        variant="persistent"
        anchor="right"
        open={open}
        data-testid='create-drawer'
      >

        <div style={{padding: '0px 15px', overflowY: 'auto', flex: 1, marginBottom: '150px'}}>
          <StyledCreateTitle>Create User</StyledCreateTitle>

          <StyledFormContainer>
            <div style={{marginTop: '3%'}}>
              <StyledFormFieldText>First Name<span style={{color: 'red'}}>*</span></StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={ e => change(e.target.value, 'first_name')}
                error={!!error?.first_name}
                helperText={error?.first_name}
                value={restaurantAdmin?.first_name}
                data-testid='create-first-name'
              />
            </div>

            <div style={{marginTop: '3%'}}>
              <StyledFormFieldText>Last Name<span style={{color: 'red'}}>*</span></StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={ e => change(e.target.value, 'last_name')}
                error={!!error?.last_name}
                helperText={error?.last_name}
                value={restaurantAdmin?.last_name}
                data-testid='create-last-name'
              />
            </div>

            <div style={{marginTop: '3%'}}>
              <StyledFormFieldText>Username<span style={{color: 'red'}}>*</span></StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={ e => change(e.target.value, 'username')}
                error={!!error?.username}
                helperText={error?.username}
                value={restaurantAdmin?.username}
                data-testid='create-username'
              />
            </div>

            <div style={{marginTop: '3%'}}>
              <StyledFormFieldText>Mobile Phone<span style={{color: 'red'}}>*</span></StyledFormFieldText>
              <StyledPhoneField
                value={restaurantAdmin?.phone_no}
                onChange={ e => change(e, 'phone_no')}
                defaultCountry="us"
                forceDialCode
                borderColor={error?.phone_no ? 'red' : '#c4c4c4'}
                data-testid='create-phone'
              />
              <StyledHelperText
                style={{display: error?.phone_no ? 'inline' : 'none'}}
              >
                {error?.phone_no && `${error?.phone_no}`}
              </StyledHelperText>
            </div>

            <div style={{marginTop: '3%'}}>
              <StyledFormFieldText>Email Address<span style={{color: 'red'}}>*</span></StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={ e => change(e.target.value, 'email')}
                error={!!error?.email}
                helperText={error?.email}
                value={restaurantAdmin?.email}
                data-testid='create-email'
              />
            </div>

            <div style={{marginTop: '3%'}}>
              <StyledFormFieldText
                style={{display: 'flex', alignItems: 'center'}}
              >
                Select Top Level Role
                <Tooltip title="Assign top level roles to the user" arrow placement="top">
                  <InfoCircleOutlined style={{fontSize: '15px', marginLeft: '5px'}}/>
                </Tooltip>
              </StyledFormFieldText>
              <FormControl sx={{ width: '100%' }}>
                <StyledInputLabel id="demo-multiple-chip-label">Roles</StyledInputLabel>
                <StyledSelect
                  labelId="demo-multiple-chip-label"
                  id="demo-multiple-chip"
                  multiple
                  value={topLevelRole}
                  onChange={handleTopLevelChange}
                  input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, padding: '10px' }}>
                      {selected.map((value) => (
                        <StyledChip
                          key={value}
                          label={capitalize(value)}
                          onDelete={() => handleTopLevelDelete(value)}
                          onMouseDown={(event => event.stopPropagation())}
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {topLevelAccess?.map((name) => (
                    <StyledMenuItem
                      key={name.value}
                      value={name.value}
                    >
                      {
                        topLevelRole?.includes(name.value) ? <SelectedMenuItem data={name?.name}/> : name?.name
                      }
                    </StyledMenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            </div>

            <div style={{marginTop: '3%'}}>
              <StyledFormFieldText
                style={{display: 'flex', alignItems: 'center'}}
              >
                Select Instance Role
                <Tooltip title="Assign instance level roles to the user" arrow placement="top">
                  <InfoCircleOutlined style={{fontSize: '15px', marginLeft: '5px'}}/>
                </Tooltip>
              </StyledFormFieldText>

              {
                instanceRole?.length === 0 ? (
                  <React.Fragment>
                    <div>
                      <StyledAddInstanceButton onClick={addInstanceRole}>
                        <AddInstanceButtonText>Add Instance Access</AddInstanceButtonText>
                      </StyledAddInstanceButton>
                    </div>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {
                      instanceRole?.map((instance) => (
                        <div key={instance?.id}>
                          <StyledInstanceContainer>
                            <StyledInstanceWrapper>
                              <div style={{height: '40px', width: '100%'}}>
                                <FormControl sx={{ minWidth: 120 }} size="small">
                                  <InputLabel style={{color: '#a6a6a6'}}>Resources</InputLabel>
                                  <StyledResourceSelect
                                    value={instance?.resourceType || ''}
                                    renderValue={() =>
                                      startCase(capitalize(instance?.resourceType))
                                    }
                                    MenuProps={{
                                      anchorOrigin: {
                                        vertical: "bottom",
                                        horizontal: "left",
                                      },
                                      transformOrigin: {
                                        vertical: "top",
                                        horizontal: "left",
                                      },
                                      PaperProps: { sx: { maxHeight: "40vh", width: '220px' } },
                                      autoFocus: false,
                                    }}
                                    label="Resource Type"
                                    onChange={(e) => handleResourceType(instance?.id, e.target.value)}
                                  >
                                    {
                                      resourceType?.map((resource) => (
                                       <StyledMenuItem
                                          key={resource.value}
                                          value={resource.value}
                                        >
                                          {
                                            instance?.resourceType === resource.value ? <SelectedMenuItem data={resource?.name} /> : resource?.name
                                          }
                                        </StyledMenuItem>
                                      ))
                                    }
                                  </StyledResourceSelect>
                                </FormControl>
                              </div>

                              <div style={{marginLeft: '-5px'}}>:</div>

                              <div style={{height: '40px', width: '100%'}}>
                                <FormControl sx={{ minWidth: 120 }} size="small">
                                  <InputLabel style={{color: '#a6a6a6'}}>Instances</InputLabel>
                                  <StyledInstanceSelect
                                    value={instance?.resourceInstance || ''}
                                    renderValue={() =>
                                      getInstanceName(instance?.resourceType, instance?.resourceInstance)
                                    }
                                    label="Instance"
                                    onChange={(e) => handleResourceInstance(instance?.id, e.target.value)}
                                    MenuProps={{
                                      PaperProps: {
                                        ref: instance?.resourceType === 'Restaurant_Group' ? restaurantGroupMenuRef : restaurantMenuRef,
                                        sx: { maxHeight: "40vh", minWidth: '230px' },
                                      },
                                      autoFocus: false,
                                      MenuListProps: { sx: { pt: 0 } },
                                    }}
                                    onClose={() => setTimeout(() => {
                                      if (instance?.resourceType === 'Restaurant_Group') {
                                        setRestaurantGroupSearch('')
                                      }

                                      if (instance?.resourceType === 'Restaurant') {
                                        setRestaurantSearch('')
                                      }
                                    }, 1000)}
                                  >
                                    <DropdownSearchBar
                                      changeSearchText={(text) => {
                                        if (instance?.resourceType === 'Restaurant_Group') {
                                          setRestaurantGroupSearch(text)
                                        }

                                        if (instance?.resourceType === 'Restaurant') {
                                          setRestaurantSearch(text)
                                        }
                                      }}
                                      placeholder={`Search ${instance?.resourceType.replace('_', ' ').toLowerCase()}`}
                                      isFetching={
                                        instance?.resourceType === 'Restaurant_Group' ? restaurantGroupFetching : restaurantFetching
                                      }
                                    />
                                    {
                                      instance?.resourceType &&
                                      getList(instance?.resourceType)
                                        .map((res) => (
                                          <StyledMenuItem
                                            key={res?.id}
                                            value={res?.id}
                                          >
                                            {
                                              `${instance?.resourceInstance}` === `${res?.id}` ? <SelectedMenuItem data={res?.value} /> : res?.value
                                            }
                                          </StyledMenuItem>
                                      ))
                                    }
                                    <DropdownInfiniteLoader
                                      hasNextPage={instance?.resourceType === 'Restaurant_Group' ? hasNextRestaurantGroupsPage : hasNextRestaurantsPage}
                                      targetRef={instance?.resourceType === 'Restaurant_Group' ? restaurantGroupLoaderRef : restaurantLoaderRef}
                                    />
                                  </StyledInstanceSelect>
                                </FormControl>
                              </div>

                              <div>#</div>

                              <div style={{height: '40px', width: '100%'}}>
                                <FormControl sx={{ minWidth: 120 }} size="small">
                                  <InputLabel style={{color: '#a6a6a6'}}>Roles</InputLabel>
                                  <StyledRoleSelect
                                    value={instance?.resourceRole || ''}
                                    renderValue={() =>
                                      startCase(capitalize(instance?.resourceRole))
                                    }
                                    label="Role"
                                    MenuProps={{
                                      PaperProps: { sx: { maxHeight: "40vh", width: '220px' } },
                                      autoFocus: false,
                                    }}
                                    onChange={(e) => handleResourceRole(instance?.id, e.target.value)}
                                  >
                                    {
                                      instance?.resourceType && resourceRole[instance?.resourceType]?.map((resource) => (
                                       <StyledMenuItem
                                          key={resource?.value}
                                          value={resource?.value}
                                        >
                                          {
                                            instance?.resourceRole === resource?.value ? <SelectedMenuItem data={resource?.name} /> : resource?.name
                                          }
                                        </StyledMenuItem>
                                      ))
                                    }
                                  </StyledRoleSelect>
                                </FormControl>
                              </div>
                            </StyledInstanceWrapper>

                            <div>
                              <ClearIcon
                                style={{cursor: 'pointer', color: '#1976d2', fontSize: '20px'}}
                                onClick={() => removeInstanceRole(instance?.id)}
                              />
                            </div>
                          </StyledInstanceContainer>
                        </div>
                      ))
                    }
                    <StyledAddInstanceButton onClick={addInstanceRole}>
                      <AddInstanceButtonText>Add Instance Access</AddInstanceButtonText>
                    </StyledAddInstanceButton>
                  </React.Fragment>
                  )
              }
            </div>
          </StyledFormContainer>

          <Paper
            sx={{
                  position: 'fixed',
                  bottom: 0,
                  right: 0,
                  width: drawerWidth,
                  height: '70px',
                  zIndex: 1
            }}
            elevation={2}
          >
            <StyledButtonContainer>
              <StyledCancelButton
                onClick={handleDrawerClose}
                variant="outlined"
              >
                Cancel
              </StyledCancelButton>
              <StyledAddButton
                onClick={handleAdd}
                variant='contained'
                disabled={status}
              >
                {status ? <LoadingSpinner isLoading={true} /> : 'Add'}
              </StyledAddButton>
            </StyledButtonContainer>
          </Paper>
        </div>
      </Drawer>

      {/* Edit Restaurant Admin Drawer */}
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            marginTop: 'calc(35px * 2)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        variant="persistent"
        anchor="right"
        open={openEdit}
        data-testid='edit-drawer'
      >
        <div style={{padding: '0px 15px', overflowY: 'auto', flex: 1, marginBottom: '150px'}}>
          <StyledCreateTitle style={{textAlign: 'center'}}>Update User</StyledCreateTitle>

          {Object.keys(updateAdmin).length > 1 ? (

          <StyledFormContainer>
            <div style={{marginTop: '3%'}}>
              <StyledFormFieldText>First Name</StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={ e => update(e.target.value, 'first_name')}
                error={!!editError?.first_name}
                helperText={editError?.first_name}
                value={updateAdmin?.first_name}
                data-testid='edit-first-name'
              />
            </div>

            <div style={{marginTop: '3%'}}>
              <StyledFormFieldText>Last Name</StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={ e => update(e.target.value, 'last_name')}
                error={!!editError?.last_name}
                helperText={editError?.last_name}
                value={updateAdmin?.last_name}
                data-testid='edit-last-name'
              />
            </div>

            <div style={{marginTop: '3%'}}>
              <StyledFormFieldText>Username</StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={ e => update(e.target.value, 'username')}
                error={!!editError?.username}
                helperText={editError?.username}
                value={updateAdmin?.username}
                data-testid='edit-username'
              />
            </div>

            <div style={{marginTop: '3%'}}>
              <StyledFormFieldText>Mobile Phone</StyledFormFieldText>
              <StyledPhoneField
                onChange={ e => update(e, 'phone_no')}
                defaultCountry="us"
                forceDialCode
                borderColor={editError?.phone_no ? 'red' : '#c4c4c4'}
                value={updateAdmin?.phone_no}
                data-testid='edit-phone'
              />
              <StyledHelperText
                style={{display: editError?.phone_no ? 'inline' : 'none'}}
              >
                {editError?.phone_no && `${editError?.phone_no}`}
              </StyledHelperText>
            </div>

            <div style={{marginTop: '3%'}}>
              <StyledFormFieldText>Email Address</StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={ e => update(e.target.value, 'email')}
                error={!!editError?.email}
                helperText={editError?.email}
                value={updateAdmin?.email}
                data-testid='edit-email'
              />
            </div>

            <div style={{marginTop: '3%'}}>
              <StyledFormFieldText
                style={{display: 'flex', alignItems: 'center'}}
              >
                Select Top Level Role
                <Tooltip title="Assign top level roles to the user" arrow placement="top">
                  <InfoCircleOutlined style={{fontSize: '15px', marginLeft: '5px'}}/>
                </Tooltip>
              </StyledFormFieldText>

              <FormControl sx={{ width: '100%' }}>
                <StyledInputLabel id="demo-multiple-chip-label">Roles</StyledInputLabel>
                <StyledSelect
                  labelId="demo-multiple-chip-label"
                  id="demo-multiple-chip"
                  multiple
                  value={updateTopLevelRole}
                  onChange={handleEditTopLevelChange}
                  input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, padding: '10px' }}>
                      {selected.map((value) => (
                        <StyledChip
                          key={value}
                          label={capitalize(value)}
                          onDelete={() => handleEditTopLevelDelete(value)}
                          onMouseDown={(event => event.stopPropagation())}
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {topLevelAccess?.map((name) => (
                    <StyledMenuItem
                      key={name.value}
                      value={name.value}
                    >
                      {
                        updateTopLevelRole?.includes(name.value) ? <SelectedMenuItem data={name?.name} /> : name?.name
                      }
                    </StyledMenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            </div>

            <div style={{marginTop: '3%'}}>
              <StyledFormFieldText
                style={{display: 'flex', alignItems: 'center'}}
              >
                Select Instance Role
                <Tooltip title="Assign instance level roles to the user" arrow placement="top">
                  <InfoCircleOutlined style={{fontSize: '15px', marginLeft: '5px'}}/>
                </Tooltip>
              </StyledFormFieldText>

              {
                instanceRole?.length === 0 ? (
                  <React.Fragment>
                    <div>
                      <StyledAddInstanceButton onClick={addInstanceRole}>
                        <AddInstanceButtonText>Add Instance Access</AddInstanceButtonText>
                      </StyledAddInstanceButton>
                    </div>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {
                      instanceRole?.map((instance) => (
                        <div key={instance?.id}>
                          <StyledInstanceContainer>
                            <StyledInstanceWrapper>
                              <div style={{height: '40px', width: '100%'}}>
                                <FormControl sx={{ minWidth: 120 }} size="small">
                                  <InputLabel style={{color: '#a6a6a6'}}>Resources</InputLabel>
                                  <StyledResourceSelect
                                    value={instance?.resourceType || ''}
                                    renderValue={() =>
                                      startCase(capitalize(instance?.resourceType))
                                    }
                                    label="Resource Type"
                                    MenuProps={{
                                      anchorOrigin: {
                                        vertical: "bottom",
                                        horizontal: "left",
                                      },
                                      transformOrigin: {
                                        vertical: "top",
                                        horizontal: "left",
                                      },
                                      PaperProps: { sx: { maxHeight: "40vh", width: '220px' } },
                                      autoFocus: false,
                                    }}
                                    onChange={(e) => handleResourceType(instance?.id, e.target.value)}
                                  >
                                    {
                                      resourceType?.map((resource) => (
                                       <StyledMenuItem
                                          key={resource.value}
                                          value={resource.value}
                                        >
                                          {
                                            instance?.resourceType === resource.value ? <SelectedMenuItem data={resource?.name} /> : resource?.name
                                          }
                                        </StyledMenuItem>
                                      ))
                                    }
                                  </StyledResourceSelect>
                                </FormControl>
                              </div>

                              <div style={{marginLeft: '-5px'}}>:</div>

                              <div style={{height: '40px', width: '100%'}}>
                                <FormControl sx={{ minWidth: 120 }} size="small">
                                  <InputLabel style={{color: '#a6a6a6'}}>Instances</InputLabel>
                                  <StyledInstanceSelect
                                    value={instance?.resourceInstance || ''}
                                    renderValue={() =>
                                      getInstanceName(instance?.resourceType, instance?.resourceInstance)
                                    }
                                    label="Instance"
                                    onChange={(e) => handleResourceInstance(instance?.id, e.target.value)}
                                    MenuProps={{
                                      PaperProps: {
                                        ref: instance?.resourceType === 'Restaurant_Group' ? restaurantGroupMenuRef : restaurantMenuRef,
                                        sx: { maxHeight: "40vh", minWidth: '230px' },
                                      },
                                      autoFocus: false,
                                      MenuListProps: { sx: { pt: 0 } },
                                    }}
                                    onClose={() => setTimeout(() => {
                                      if (instance?.resourceType === 'Restaurant_Group') {
                                        setRestaurantGroupSearch('')
                                      }

                                      if (instance?.resourceType === 'Restaurant') {
                                        setRestaurantSearch('')
                                      }
                                    }, 1000)}
                                  >
                                    <DropdownSearchBar
                                      changeSearchText={(text) => {
                                        if (instance?.resourceType === 'Restaurant_Group') {
                                          setRestaurantGroupSearch(text)
                                        }

                                        if (instance?.resourceType === 'Restaurant') {
                                          setRestaurantSearch(text)
                                        }
                                      }}
                                      placeholder={`Search ${instance?.resourceType.replace('_', ' ').toLowerCase()}`}
                                      isFetching={
                                        instance?.resourceType === 'Restaurant_Group' ? restaurantGroupFetching : restaurantFetching
                                      }
                                    />
                                    {
                                      instance?.resourceType &&
                                      getList(instance?.resourceType)
                                        .map((res) => (
                                          <StyledMenuItem
                                            key={res?.id}
                                            value={res?.id}
                                          >
                                            {
                                              `${instance?.resourceInstance}` === `${res?.id}` ? <SelectedMenuItem data={res?.value} /> : res?.value
                                            }
                                          </StyledMenuItem>
                                      ))
                                    }
                                    <DropdownInfiniteLoader
                                      hasNextPage={instance?.resourceType === 'Restaurant_Group' ? hasNextRestaurantGroupsPage : hasNextRestaurantsPage}
                                      targetRef={instance?.resourceType === 'Restaurant_Group' ? restaurantGroupLoaderRef : restaurantLoaderRef}
                                    />
                                  </StyledInstanceSelect>
                                </FormControl>
                              </div>

                              <div>#</div>

                              <div style={{height: '40px', width: '100%'}}>
                                <FormControl sx={{ minWidth: 120 }} size="small">
                                  <InputLabel style={{color: '#a6a6a6'}}>Roles</InputLabel>
                                  <StyledRoleSelect
                                    value={instance?.resourceRole || ''}
                                    renderValue={() =>
                                      startCase(capitalize(instance?.resourceRole))
                                    }
                                    label="Role"
                                    MenuProps={{
                                      PaperProps: { sx: { maxHeight: "40vh", width: '220px' } },
                                      autoFocus: false,
                                    }}
                                    onChange={(e) => handleResourceRole(instance?.id, e.target.value)}
                                  >
                                    {
                                      instance?.resourceType && resourceRole[instance?.resourceType]?.map((resource) => (
                                       <StyledMenuItem
                                          key={resource?.value}
                                          value={resource?.value}
                                        >
                                          {
                                            instance?.resourceRole === resource?.value ? <SelectedMenuItem data={resource?.name} /> : resource?.name
                                          }
                                        </StyledMenuItem>
                                      ))
                                    }
                                  </StyledRoleSelect>
                                </FormControl>
                              </div>
                            </StyledInstanceWrapper>

                            <div>
                              <ClearIcon
                                style={{cursor: 'pointer', color: '#1976d2', fontSize: '20px'}}
                                onClick={() => removeInstanceRole(instance?.id)}
                              />
                            </div>
                          </StyledInstanceContainer>
                        </div>
                      ))
                    }
                    <StyledAddInstanceButton onClick={addInstanceRole}>
                      <AddInstanceButtonText>Add Instance Access</AddInstanceButtonText>
                    </StyledAddInstanceButton>
                  </React.Fragment>
                  )
              }
            </div>
          </StyledFormContainer>
          ): (
            <Box sx={{ position: "absolute", left: "45%", top: "40%" }}>
              <Loader />
            </Box>
          )}

          <Paper
            sx={{
                  position: 'fixed',
                  bottom: 0,
                  right: 0,
                  width: drawerWidth,
                  height: '70px',
                  zIndex: 1
            }}
            elevation={2}
          >
            <StyledButtonContainer>
              <StyledCancelButton
                onClick={handleEditClose}
                variant="outlined"
              >
                Cancel
              </StyledCancelButton>

              {Object.keys(updateAdmin).length > 1 &&
                <StyledUpdateButton
                  onClick={handleUpdate}
                  variant='contained'
                  disabled={status}
                >
                  { status ? <LoadingSpinner isLoading={true} /> : 'Update'}
                </StyledUpdateButton>
              }
            </StyledButtonContainer>
          </Paper>
        </div>
      </Drawer>

    </RestaurantAdminContainer>
  );
}
